import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  BodyWrapper,
  ActionObject,
  Header,
  Loader,
  MainWrapper,
} from '@components';
import {
  blogCategoriesQuery,
  blogCategoryDeleteMutation,
  blogCategoryMutation,
  blogCategoryQuery,
  siteQuery,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import BlogCategoryForm from '../components/BlogCategoryForm/BlogCategoryForm.jsx';
import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';

export const BlogCategory = () => {
  const { t, i18n } = useTranslation();
  const { blogCategoryID, siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // get blogCategory
  const {
    data: blogCategoryData,
    isPending: blogCategoryIsPending,
    isError,
  } = useQuery(blogCategoryQuery(blogCategoryID));

  // save blogCategory
  const mutation = useMutation(
    blogCategoryMutation(blogCategoryID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['categories', 'list'] });
        // invalidate the detail for revisits
        queryClient.invalidateQueries({
          queryKey: ['categories', 'detail', blogCategoryID],
        });
      },
    }),
  );

  // delete blogCategory
  const deleteMutation = useMutation(
    blogCategoryDeleteMutation(blogCategoryID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['categories', 'list'] });
        // navigate to the list view
        navigate(`/dashboard/sites/${siteID}/blogs/categories`);
      },
    }),
  );

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <Link to={`/dashboard/sites/${siteID}/blogs`}>Blogs</Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              <Link to={`/dashboard/sites/${siteID}/blogs/categories`}>
                {t('Blog categories')}
              </Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              {blogCategoryIsPending && <span>...</span>}
              {blogCategoryData && <span>{blogCategoryData.name}</span>}
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            {blogCategoryIsPending && (
              <>
                <h1>...</h1>
                <Loader className="sb-loader sb-loader-large" />
              </>
            )}
            {blogCategoryData && !isError && (
              <>
                <BlogCategoryForm
                  blogCategoryData={blogCategoryData}
                  mutation={mutation}
                  disabled={mutation.isPending || deleteMutation.isPending}
                  siteID={siteID}
                />
                <div className="sb-message-action-section">
                  <ActionObject
                    onAction={() => deleteMutation.mutate()}
                    loading={deleteMutation.isPending}
                    actionName="Delete"
                    actionVerb="Deleting"
                    label="Delete category"
                    note="This blog category will be permanently deleted"
                  />
                </div>
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
